/*
 * Slice of state for a list of the users layouts
 */

import ManagedLayout from 'layoutManagement/ManagedLayout';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Services from 'store/Services';
import { useAppSelector } from 'store/hooks';
import State from 'store/State';
import { IndigoDispatch } from 'store/store';

export interface LayoutListState {
    layouts: ManagedLayout[];
    status: 'idle' | 'pending' | 'succeeded' | 'failed';
    error?: unknown;
}

export type LayoutListsState = { [origin: string]: LayoutListState };

export const initialLayoutListState: LayoutListState = {
    layouts: [],
    status: 'idle',
};

export const initialLayoutListsState: LayoutListsState = {};

const getLayoutsAsyncThunk = createAsyncThunk(
    'get-layouts',
    async (args: { origin: string; forceReload: boolean }, thunkApi) => {
        const { layoutService, loggingService } = thunkApi.extra as Services;
        const { origin } = args;

        loggingService.debug(
            'getLayoutsAsyncThunk',
            'Loading layouts for ' + args.origin
        );

        try {
            return await layoutService.getAllLayoutsWithSharesForOrigin(origin);
        } catch (err) {
            return thunkApi.rejectWithValue(err);
        }
    }
);

export const getLayouts =
    (args: { origin: string; forceReload: boolean }) =>
    async (dispatch: IndigoDispatch, getState: () => State) => {
        const state = getState() as State;
        const { forceReload } = args;

        if (!forceReload) {
            const layouts = selectLayoutListState(state, args.origin);
            // Not forcing reload, so only load if haven't kicked one off already...
            if (layouts.status !== 'idle') {
                return;
            }
        }
        dispatch(getLayoutsAsyncThunk(args));
    };

const layoutsListSlice = createSlice({
    name: 'layoutsList',
    initialState: initialLayoutListsState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getLayoutsAsyncThunk.pending, (state, action) => {
            state[action.meta.arg.origin] = state[action.meta.arg.origin] ?? {
                ...initialLayoutListState,
            };
            state[action.meta.arg.origin].error = undefined;
            state[action.meta.arg.origin].status = 'pending';
        });
        builder.addCase(getLayoutsAsyncThunk.fulfilled, (state, action) => {
            if (action.payload) {
                state[action.meta.arg.origin] = state[
                    action.meta.arg.origin
                ] ?? { ...initialLayoutListState };
                state[action.meta.arg.origin].layouts = action.payload;
                state[action.meta.arg.origin].error = undefined;
                state[action.meta.arg.origin].status = 'succeeded';
            }
        });
        builder.addCase(getLayoutsAsyncThunk.rejected, (state, action) => {
            state[action.meta.arg.origin] = state[action.meta.arg.origin] ?? {
                ...initialLayoutListState,
            };
            state[action.meta.arg.origin].error = action.payload;
            state[action.meta.arg.origin].status = 'failed';
        });
    },
});

export function selectLayoutListState(state: State, origin: string) {
    return state.layoutList[origin] ?? initialLayoutListState;
}

export function selectLayoutList(state: State, origin: string) {
    return selectLayoutListState(state, origin).layouts;
}

export const useSelectLayoutList = (origin: string) =>
    useAppSelector((s) => selectLayoutList(s, origin));

export const actionTypes = {
    pending: getLayoutsAsyncThunk.pending.type,
    fulfilled: getLayoutsAsyncThunk.fulfilled.type,
    rejected: getLayoutsAsyncThunk.rejected.type,
};
export default layoutsListSlice.reducer;
